import {
  ShieldCheck,
  FileText,
  Users2,
  Scale,
  ClipboardCheck,
} from "lucide-react";
import type { BlogPost } from "../types";

export const tenantScreeningGuide: BlogPost = {
  slug: "tenant-screening-guide",
  title: "The Owner's Guide to Tenant Screening: Protecting Your Asset",
  excerpt:
    "Why credit scores alone aren't enough and how comprehensive tenant screening can help reduce risk, prevent costly tenant problems, and protect your rental property.",
  date: "Sept 8th, 2026",
  category: "Property Management",
  readTime: "6 min read",
  author: {
    name: "Feingold Property Management Team",
    location: "Laurel, MD & Mid-Atlantic Region",
    initials: "FP",
  },
  intro: [
    "Finding a tenant is relatively easy. Finding the right tenant requires a more thoughtful process.",
    "For rental property owners throughout Maryland, Washington DC, and Northern Virginia, tenant screening is one of the most important parts of residential property management. A strong screening process can help reduce late payments, property damage, lease violations, and costly turnover.",
    "While credit history can provide useful information, it should only be one part of a comprehensive tenant screening process.",
    "Here are several factors property owners should consider.",
  ],
  sections: [
    {
      number: "1",
      icon: ShieldCheck,
      title: "Look Beyond the Credit Score",
      paragraphs: [
        "A credit score provides a snapshot of an applicant's credit history, but it doesn't tell the entire story.",
        "Property owners and professional property management companies may also evaluate income verification, rental history, payment patterns, debt obligations, and other legally permissible information.",
        "The goal is to develop a more complete picture of an applicant's ability to meet the responsibilities of the lease rather than relying on a single number.",
      ],
    },
    {
      number: "2",
      icon: FileText,
      title: "Verify Income and Employment",
      paragraphs: [
        "Consistent rental income begins with selecting tenants who have the financial ability to pay rent.",
        "Income and employment verification can help confirm that the information submitted on a rental application is accurate.",
        "Depending on applicable laws and screening criteria, documentation may include pay stubs, employment verification, tax documents, bank statements, or other proof of income.",
        "For landlords managing rental properties in Maryland, Washington DC, and Northern Virginia, establishing consistent written qualification standards can also make the screening process more organized.",
      ],
    },
    {
      number: "3",
      icon: Users2,
      title: "Review Rental History",
      paragraphs: [
        "Previous rental behavior can provide valuable information.",
        "When appropriate and legally permitted, landlords or property managers may verify previous residences and contact prior landlords to confirm rental history.",
        "Questions may include whether rent was paid consistently, whether lease requirements were followed, and whether the property was properly maintained.",
        "Past behavior cannot guarantee future performance, but rental history can provide additional context when evaluating an application.",
      ],
    },
    {
      number: "4",
      icon: Scale,
      title: "Follow Fair Housing and Local Regulations",
      paragraphs: [
        "Tenant screening isn't only about finding qualified applicants. It must also be conducted in compliance with applicable housing laws.",
        "Federal Fair Housing requirements apply across the region, while Maryland, Washington DC, and Virginia landlord-tenant laws can add additional requirements.",
        "Screening criteria should be documented, consistently applied, and reviewed as laws and regulations change.",
        "This is one area where experienced local property management can be particularly valuable.",
      ],
    },
    {
      number: "5",
      icon: ClipboardCheck,
      title: "Create a Consistent Screening Process",
      paragraphs: [
        "Good tenant screening shouldn't change depending on who submits the application.",
        "Establish written rental criteria and use a repeatable process for evaluating applicants. Consistency can help owners make better decisions while reducing unnecessary risk.",
        "Technology can also help organize applications, verification, documentation, and communication throughout the leasing process.",
      ],
    },
  ],
  conclusion: {
    title: "Protect Your Rental Investment",
    paragraphs: [
      "Your rental property is more than an address. It's an investment.",
      "For property owners seeking residential property management in Maryland, Washington DC, or Northern Virginia, professional tenant screening can be an important part of protecting that investment.",
      "Strong property management begins long before the tenant receives the keys.",
    ],
    highlight:
      "It begins with choosing tenants through a careful, consistent, and legally compliant process.",
  },
};
